/**
 * getGapCategoriesForType — Workflow Prompt 6.
 *
 * Buckets a flat list of data-gap items under type-specific groupings so each
 * report type's rerun panel can render gaps under domain-relevant headings.
 * Pure / synchronous / no React — easy to unit-test.
 *
 * Bucket assignment is keyword-based against the gap's field + description.
 * Anything that doesn't match a known bucket lands in the type's default
 * "Other" bucket.
 */
import type { ReportTypeId } from '../config/reportTypes';

export interface GapItem {
  field: string;
  description: string;
}

export interface GapCategoryGroup<T extends GapItem = GapItem> {
  /** Bucket label shown in the UI. */
  label: string;
  /** Gaps in this bucket. */
  items: T[];
}

type Matcher = (g: GapItem) => boolean;

interface BucketDef {
  label: string;
  match: Matcher;
}

/**
 * Bucket definitions per type. The `fallback` bucket catches anything not
 * matched by an earlier bucket; it MUST always exist last.
 */
const BUCKETS_BY_TYPE: Record<ReportTypeId, BucketDef[]> = {
  feasibility: [
    { label: 'Other', match: () => true },
  ],
  'power-availability': [
    { label: 'Grid capacity',         match: containsAny(['grid', 'substation', 'capacity', 'voltage', 'transmission', 'hifld']) },
    { label: 'Interconnection queue', match: containsAny(['queue', 'interconnection', 'lgia', 'sgia']) },
    { label: 'Rate data',             match: containsAny(['rate', 'pricing', 'tariff', 'eia', 'electricity']) },
    { label: 'BTM tech',              match: containsAny(['btm', 'lcoe', 'capex', 'opex', 'storage', 'bess']) },
    { label: 'Renewables',            match: containsAny(['solar', 'wind', 'renewable', 'nrel', 'pv']) },
    { label: 'Other',                 match: () => true },
  ],
  'water-resource': [
    { label: 'Supply',     match: containsAny(['supply', 'aquifer', 'groundwater', 'surface water', 'reservoir', 'municipal']) },
    { label: 'Rights',     match: containsAny(['rights', 'doctrine', 'appropriation', 'riparian']) },
    { label: 'Discharge',  match: containsAny(['discharge', 'wastewater', 'npdes', 'zld', 'effluent', '316(b)']) },
    { label: 'Drought',    match: containsAny(['drought', 'aqueduct', 'stress', 'precipitation']) },
    { label: 'Other',      match: () => true },
  ],
  environmental: [
    { label: 'NEPA',       match: containsAny(['nepa', 'ceqa', 'catex', '40 cfr', 'eis', 'environmental assessment']) },
    { label: 'Species',    match: containsAny(['species', 'habitat', 'esa', 'usfws', 'endangered']) },
    { label: 'Air',        match: containsAny(['air', 'aqi', 'attainment', 'pollutant', 'pm2.5', 'naaqs']) },
    { label: 'Water',      match: containsAny(['water', 'wetland', 'flood', 'stormwater']) },
    // Tribal must come BEFORE Cultural — NHPA Section 106 mentions both
    // tribal consultation and the NHPA statute, but the gap's intent is
    // tribal coordination so it should bucket there.
    { label: 'Tribal',     match: containsAny(['tribal', 'indigenous', 'native']) },
    { label: 'Cultural',   match: containsAny(['cultural', 'historic', 'shpo', 'archaeological', 'nhpa', 'cemetery']) },
    { label: 'Other',      match: () => true },
  ],
  'test-fit': [
    { label: 'Setbacks',   match: containsAny(['setback', 'easement', 'buffer']) },
    { label: 'Topography', match: containsAny(['topography', 'slope', 'elevation', 'grading', 'land']) },
    { label: 'Stormwater', match: containsAny(['stormwater', 'detention', 'drainage', 'impervious']) },
    { label: 'Zoning',     match: containsAny(['zoning', 'parcel', 'land use', 'regrid']) },
    { label: 'Other',      match: () => true },
  ],
  interconnection: [
    { label: 'Fiber',      match: containsAny(['fiber', 'lit', 'dark', 'route', 'conduit']) },
    { label: 'Latency',    match: containsAny(['latency', 'rtt', 'ms ', 'aws', 'azure', 'gcp', 'cloud']) },
    { label: 'Carriers',   match: containsAny(['carrier', 'level 3', 'lumen', 'zayo', 'crown castle', 'diversity']) },
    { label: 'IX',         match: containsAny(['ix', 'peering', 'exchange', 'peeringdb']) },
    { label: 'Other',      match: () => true },
  ],
  // PSR-1 (row 0.99290) — Public Sentiment Report. PSR-2 will widen the
  // bucket list as the SENTIMENT INDEX composer surfaces specific gap
  // categories (provider down, trend window too short, etc.).
  'public-sentiment': [
    { label: 'Stakeholder coverage', match: containsAny(['stakeholder', 'tribal', 'council', 'community', 'neighbourhood', 'neighborhood']) },
    { label: 'Public records',       match: containsAny(['minutes', 'docket', 'regulations.gov', 'ferc', 'adams', 'gdelt', 'municipal']) },
    { label: 'Other',                match: () => true },
  ],
  'custom-brief': [
    { label: 'Other', match: () => true },
  ],
  'executive-summary': [
    { label: 'Other', match: () => true },
  ],
  'supply-chain-risk': [
    { label: 'Other', match: () => true },
  ],
  // FIN-1 (wf w7nuyquwd) — Financial Viability Report gap buckets.
  financial: [
    { label: 'Cost / CAPEX',   match: containsAny(['capex', 'cost', 'epc', 'overnight', 'capacity factor']) },
    { label: 'Financing',      match: containsAny(['wacc', 'debt', 'equity', 'discount', 'hurdle', 'irr', 'tax', 'itc', 'ptc']) },
    { label: 'Revenue / PPA',  match: containsAny(['ppa', 'revenue', 'price', 'offtake', 'escalator', 'tariff']) },
    { label: 'Other',          match: () => true },
  ],
};

/**
 * Builds a matcher that returns true when any keyword appears as a whole word
 * in the gap's `field` or `description`. Word-boundary matching avoids false
 * positives like "rate" matching "unrelated" or "queue" matching "querulous".
 */
function containsAny(keywords: string[]): Matcher {
  const patterns = keywords.map(k => {
    const escaped = k.toLowerCase().replace(/[-/\\^$*+?.()|[\]{}]/g, '\\$&');
    return new RegExp(`(^|[^a-z0-9])${escaped}([^a-z0-9]|$)`, 'i');
  });
  return (g: GapItem) => {
    const haystack = ` ${g.field} ${g.description} `.toLowerCase();
    return patterns.some(p => p.test(haystack));
  };
}

/**
 * Group `gaps` into type-specific buckets. Buckets are returned in the
 * type's defined order. Empty buckets are omitted from the output.
 */
export function getGapCategoriesForType<T extends GapItem>(
  reportType: ReportTypeId | undefined | null,
  gaps: T[],
): GapCategoryGroup<T>[] {
  const buckets = BUCKETS_BY_TYPE[reportType ?? 'feasibility'] ?? BUCKETS_BY_TYPE.feasibility;
  const result: GapCategoryGroup<T>[] = buckets.map(b => ({ label: b.label, items: [] as T[] }));
  for (const gap of gaps) {
    const idx = buckets.findIndex(b => b.match(gap));
    // findIndex always finds because the last bucket is the catch-all.
    result[idx >= 0 ? idx : result.length - 1].items.push(gap);
  }
  return result.filter(g => g.items.length > 0);
}
